/**
 * <kb-waitlist-storytelling>
 *
 * Scroll controller for the waitlist storytelling section. A single GSAP
 * ScrollTrigger (no GSAP pin) scrubs a progress value (0..1). That one progress
 * value drives everything:
 *   1. the background frame sequence (<background-frame-animation>.seek)
 *   2. the active text segment (cross-faded heading/body)
 *   3. the active indicator dot
 *
 * Pinning is done in CSS, not by GSAP: a tall runway + a single position: sticky
 * child (height lvh, the bar-hidden viewport) hold the frame canvas and the text
 * in view. Sticky is browser-native (no DOM reparenting, no pin jitter) and rides
 * out with the runway on its own, so nothing has to be toggled on or off. The lvh
 * height is stable, so the canvas never resizes as the iOS bar moves; the
 * bottom-anchored copy is lifted by the bar delta in CSS so it stays above the bar.
 *
 * GSAP + ScrollTrigger are pulled in by dynamic import (@theme/gsap,
 * @theme/scrolltrigger) only when this element connects, so they never load on
 * pages without the section and there is no global window.gsap and no readiness
 * polling. The frame renderer is library-agnostic.
 */

const CONTROLLER_CONFIGURATION = {
  // Text cross-fade. Falls back to the section's CSS animation tokens when set.
  textCrossfadeDurationSeconds: 0.5,
  textCrossfadeEase: 'power2.out',
  fallbackTextRevealDistancePx: 24,
  // Inset (px) of the floated-to-top text from the top of the banner. The text rests
  // at the bottom of the dvh box (in flow) and is lifted by translateY for the top
  // position; this is where its top lands. Matches the old pinned top anchor (the
  // former `.is-pinned .is-top { top: 64px }`), so removing the fixed pin did not
  // move the top float.
  floatTopMarginPx: 64,
  // Fade duration (seconds) for moving the text between its two fixed positions.
  // The text fades out, jumps to the new position while hidden, then fades in.
  floatFadeDurationSeconds: 0.35,
  // End-of-scroll hold. The whole frame sequence plays across the first
  // (1 - tailHoldFraction) of the runway, then sits parked on its final frame
  // through this tail. On iOS the inertial scroll decelerates and rubber-bands
  // at the very bottom of the runway; with scrub tied 1:1 to scroll position
  // those last frames otherwise crawl one by one in time with the deceleration.
  // Parking the final frame across the tail moves that deceleration onto a frame
  // that is already frozen, so the crawl is invisible. END ONLY: the start stays
  // 1:1, so this is NOT the old symmetric edge-hold. Set to 0 to disable (pure
  // linear scrub, like GSAP's imageSequence helper).
  // Keep this SMALL. The dots/segments run on RAW progress (so authored
  // enter-at percentages keep their meaning), while the frames run on this
  // buffered progress. A large tail (e.g. 0.4) freezes the frame across the
  // last 40% of the runway AND drops the final auto-spaced segment (3 blocks =>
  // enter at 0.667 raw) into that frozen zone, so the last dot is "stuck" over a
  // multi-screen dead scroll at the end. 0.15 is just enough to swallow the iOS
  // end-of-runway inertial crawl while keeping every segment inside the moving
  // part of the runway.
  tailHoldFraction: 0,
};

class KBWaitlistStorytelling extends HTMLElement {
  #abortController = new AbortController();

  /** @type {import('./background-frame-animation.js').BackgroundFrameAnimation | null} */
  #frameAnimation = null;
  /** @type {HTMLElement[]} */
  #textBlocks = []; 
  /** @type {HTMLElement[]} */
  #indicators = [];
  /** @type {number[]} */
  #segmentEnterProgress = [];
  #activeSegmentIndex = -1;

  #scrollTrigger = null;
  /** @type {import('gsap').gsap | null} The gsap instance, once imported. */
  #gsap = null;
  #textRevealDistancePx = CONTROLLER_CONFIGURATION.fallbackTextRevealDistancePx;

  /** @type {HTMLElement | null} The text container that floats up over the banner. */
  #textStack = null;
  // Max upward travel for the float, in px: the distance from the text's resting
  // (bottom) position to the top of the banner, less a small margin. The float
  // percentage is taken of THIS, so 100% lands at the top and the text can never
  // leave the band. Measured lazily, invalidated to 0 on resize.
  #textStackTravelPx = 0;
  #floatAtTop = false; // current discrete float position (false = bottom, true = top)
  // Original frame numbering (the desktop set), read from the engine element.
  // Float points and emphasis ranges are authored against these numbers, so they
  // mean the same thing regardless of mobile frame thinning.
  #frameCount = 0;
  #frameStartIndex = 1;
  /** @type {{ progress: number, percent: number }[]} Float keyframes, sorted by progress. */
  #floatPoints = [];
  /** @type {MediaQueryList | null} Float is mobile only; this matches on small screens. */
  #floatMediaQuery = null;
  // Emphasis warp lookup. #emphasisScrollPositions[i] is the scrub progress at
  // which frame i sits; #emphasisFrameProgress[i] is i/(N-1). Both null when no
  // emphasis is configured (then the warp is the identity).
  /** @type {number[] | null} */
  #emphasisScrollPositions = null;
  /** @type {number[] | null} */
  #emphasisFrameProgress = null;

  async connectedCallback() {
    this.#frameAnimation = this.querySelector('background-frame-animation');
    this.#textBlocks = Array.from(this.querySelectorAll('[data-kb-text-index]'));
    this.#indicators = Array.from(this.querySelectorAll('[data-kb-indicator]'));
    this.#textStack = this.querySelector('.kb-waitlist-storytelling__text-stack');

    if (this.#textBlocks.length === 0) return;

    this.#segmentEnterProgress = this.#computeSegmentEnterProgress();
    this.#textRevealDistancePx = this.#readRevealDistance();
    this.#readFrameNumbering();
    this.#floatMediaQuery = window.matchMedia('(max-width: 749px)');
    this.#floatPoints = this.#parseFloatPoints();
    this.#buildEmphasisWarp();

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      this.#applyStaticState();
      return;
    }

    // GSAP + ScrollTrigger are fetched here, on demand, so they load only on a
    // page that actually has this element — never site-wide — with no global
    // window.gsap and no readiness polling. registerPlugin (below) hands
    // ScrollTrigger this same gsap instance, so the scrub runs on one core.
    const [{ gsap }, { ScrollTrigger }] = await Promise.all([
      import('@theme/gsap'),
      import('@theme/scrolltrigger'),
    ]);
    if (this.#abortController.signal.aborted) return;
    this.#gsap = gsap;

    this.#setUpScrollAnimation(gsap, ScrollTrigger);
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#scrollTrigger?.kill();
  }

  /* ----- Scroll animation ----- */

  #setUpScrollAnimation(gsap, ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);

    ScrollTrigger.config({
      // iOS fires resize as the browser bar shows/hides; ignore those so they
      // cannot trigger a refresh mid-scroll. Orientation changes still refresh.
      ignoreMobileResize: true,
      // Drop the `load` event from auto-refresh. By default ScrollTrigger refreshes
      // on window `load`, which waits for every one of the 700 frame images — so it
      // fires seconds in, mid-scroll, and that refresh jumps the page. Our layout
      // never changes as images load (the canvas is a fixed CSS size), so
      // DOMContentLoaded + resize are all we need.
      autoRefreshEvents: 'visibilitychange,DOMContentLoaded,resize',

      
    });

    // Start on the first segment without animating in.
    this.#activateSegment(0, { animate: false });

    // No GSAP `pin`: the CSS runway + sticky child do the pinning, and
    // ScrollTrigger only scrubs a 0..1 progress across the runway. start/end span
    // the runway: progress 0 when its top reaches the viewport top, 1 when its
    // bottom reaches the viewport bottom.
    this.#scrollTrigger = ScrollTrigger.create({
      trigger: this,
      start: 'top top',
      end: 'bottom bottom',
      // Direct link (no smoothing), exactly like GSAP's imageSequence helper: the
      // frame tracks the scrollbar 1:1 with no catch-up tail.
      scrub: true,
      onUpdate: (self) => this.#handleProgress(self.progress),
    });

    // The float travel only changes on a real resize or orientation change (a
    // stable height, not the dynamic bar), so invalidate it on resize and
    // re-measure lazily on the next frame, never a forced reflow during the bar
    // animation. Bound to the abort signal, so it is cleaned up on disconnect.
    window.addEventListener('resize', () => {
      this.#textStackTravelPx = 0;
    }, { signal: this.#abortController.signal });

    // Paint the frame + segment for the current scroll position before any user
    // scroll, so the correct frame shows at load (frame 0 at the top, the right
    // frame when reloaded mid-section) instead of waiting for the first onUpdate.
    this.#handleProgress(this.#scrollTrigger.progress);
  }

  /* ----- Text position (two fixed positions; hide -> move -> show) ----- */

  /**
   * Move the text between its two FIXED positions: bottom (the resting spot,
   * --kb-text-lift 0) and top (lifted by the measured travel). The text does NOT
   * slide between them; it fades out, jumps to the new position while hidden,
   * then fades back in. The top position is rendered white (via .is-top) for
   * readability over the darker frames.
   * @param {boolean} top target position
   * @param {{ animate: boolean }} options
   */
  #setFloatPosition(top, { animate }) {
    this.#floatAtTop = top;
    const stack = this.#textStack;
    if (!stack) return;

    if (this.#textStackTravelPx === 0) this.#measureFloatTravel();
    const gsap = this.#gsap;

    if (!animate || !gsap) {
      stack.classList.toggle('is-top', top);
      this.style.setProperty('--kb-text-lift', `${top ? this.#textStackTravelPx : 0}px`);
      return;
    }

    const { floatFadeDurationSeconds, textCrossfadeEase } = CONTROLLER_CONFIGURATION;
    gsap.killTweensOf(stack);
    gsap.to(stack, {
      autoAlpha: 0,
      duration: floatFadeDurationSeconds,
      ease: textCrossfadeEase,
      overwrite: 'auto',
      onComplete: () => {
        const atTop = this.#floatAtTop;
        stack.classList.toggle('is-top', atTop);
        this.style.setProperty('--kb-text-lift', `${atTop ? this.#textStackTravelPx : 0}px`);
        gsap.to(stack, {
          autoAlpha: 1,
          duration: floatFadeDurationSeconds,
          ease: textCrossfadeEase,
          overwrite: 'auto',
        });
      },
    });
  }

  #handleProgress(progress) {
    // The frame mapping compresses scroll so the sequence finishes by
    // (1 - tailHoldFraction) of the runway and then holds the final frame across
    // the tail, where iOS inertial scroll decelerates (see tailHoldFraction). The
    // emphasis warp on top is the identity unless slow ranges are set. The text
    // float tracks this same mapped value, so a "frame N" float point lands when
    // frame N is actually shown, and the float parks with the frame in the tail.
    // Segments (text crossfade + dots) stay on the RAW progress so authored
    // enter-at percentages keep their meaning; the crossfade is already debounced.
    const frameProgress = this.#mapProgressToFrame(progress);
    this.#frameAnimation?.seek(frameProgress);
    this.#updateTextFloat(frameProgress);

    const segmentIndex = this.#segmentForProgress(progress);
    if (segmentIndex !== this.#activeSegmentIndex) {
      this.#activateSegment(segmentIndex, { animate: true });
    }
  }

  /** Raw scrub progress -> displayed frame progress (tail hold, then emphasis warp). */
  #mapProgressToFrame(progress) {
    return this.#applyEmphasisWarp(this.#bufferScrollProgress(progress));
  }

  /**
   * Compress raw scrub progress so the sequence reaches its final frame at
   * (1 - tailHoldFraction) and stays there through the tail. See
   * CONTROLLER_CONFIGURATION.tailHoldFraction for why (iOS end-of-runway crawl).
   * @param {number} progress 0..1 raw scrub progress
   */
  #bufferScrollProgress(progress) {
    const active = 1 - CONTROLLER_CONFIGURATION.tailHoldFraction;
    return active > 0 ? Math.min(1, progress / active) : 1;
  }

  /**
   * Slow down the configured frame ranges by remapping progress through the
   * precomputed weight curve (see #buildEmphasisWarp). Identity when no emphasis
   * is set. Input and output are both 0..1 frame progress.
   * @param {number} progress 0..1
   */
  #applyEmphasisWarp(progress) {
    const scrollPositions = this.#emphasisScrollPositions;
    const frameProgress = this.#emphasisFrameProgress;
    if (!scrollPositions || scrollPositions.length < 2) return progress;
    if (progress <= scrollPositions[0]) return frameProgress[0];
    const lastIndex = scrollPositions.length - 1;
    if (progress >= scrollPositions[lastIndex]) return frameProgress[lastIndex];

    let low = 0;
    let high = lastIndex;
    while (high - low > 1) {
      const mid = (low + high) >> 1;
      if (scrollPositions[mid] <= progress) low = mid;
      else high = mid;
    }
    const span = scrollPositions[high] - scrollPositions[low] || 1;
    const t = (progress - scrollPositions[low]) / span;
    return frameProgress[low] + (frameProgress[high] - frameProgress[low]) * t;
  }

  /* ----- Text position (two fixed positions, fade between) ----- */

  /**
   * Pick the text's target position from the authored float points and move it
   * there when it changes. The float percentage is quantised to two positions
   * (>= 50 = top, else bottom); crossing between them fades the text out, moves
   * it, and fades it back in (see #setFloatPosition). No continuous sliding.
   * Mobile only. No-op when no float points are configured.
   * @param {number} frameProgress 0..1
   */
  #updateTextFloat(frameProgress) {
    if (this.#floatPoints.length === 0 || !this.#textStack) return;

    // Mobile only: on wider screens keep the text in its base bottom position.
    if (!this.#floatMediaQuery?.matches) {
      if (this.#floatAtTop) this.#setFloatPosition(false, { animate: false });
      return;
    }

    const percent = this.#floatPercentAt(frameProgress);
    const targetTop = percent >= 50;
    if (targetTop !== this.#floatAtTop) this.#setFloatPosition(targetTop, { animate: true });
  }

  /** Linear interpolation of the float percentage across #floatPoints (sorted by progress). */
  #floatPercentAt(frameProgress) {
    const points = this.#floatPoints;
    if (frameProgress <= points[0].progress) return points[0].percent;
    const lastIndex = points.length - 1;
    if (frameProgress >= points[lastIndex].progress) return points[lastIndex].percent;

    for (let index = 1; index < points.length; index += 1) {
      if (frameProgress <= points[index].progress) {
        const previous = points[index - 1];
        const current = points[index];
        const span = current.progress - previous.progress || 1;
        const t = (frameProgress - previous.progress) / span;
        return previous.percent + (current.percent - previous.percent) * t;
      }
    }
    return points[lastIndex].percent;
  }

  #measureFloatTravel() {
    // offsetTop is the layout distance from the positioned ancestor (the pinned
    // band) to the text's resting top. It ignores the float transform, so it is
    // always the baseline. The float can travel up to that, less a small margin.
    const restingTop = this.#textStack?.offsetTop ?? 0;
    this.#textStackTravelPx = Math.max(0, restingTop - CONTROLLER_CONFIGURATION.floatTopMarginPx);
  }

  /* ----- Segments (text + dots) ----- */

  #computeSegmentEnterProgress() {
    const segmentCount = this.#textBlocks.length;
    return this.#textBlocks.map((textBlock, index) => {
      const enterAtPercent = Number(textBlock.dataset.kbEnterAt);
      if (Number.isFinite(enterAtPercent)) {
        return Math.min(1, Math.max(0, enterAtPercent / 100));
      }
      return index / segmentCount;
    });
  }

  #segmentForProgress(progress) {
    let activeIndex = 0;
    for (let index = 0; index < this.#segmentEnterProgress.length; index += 1) {
      if (progress >= this.#segmentEnterProgress[index]) {
        activeIndex = index;
      } else {
        break;
      }
    }
    return activeIndex;
  }

  #activateSegment(segmentIndex, { animate }) {
    if (segmentIndex === this.#activeSegmentIndex) return;

    const previousIndex = this.#activeSegmentIndex;
    this.#activeSegmentIndex = segmentIndex;

    this.#indicators.forEach((indicator, index) => {
      indicator.classList.toggle('is-active', index === segmentIndex);
    });

    const gsap = this.#gsap;
    if (!animate || !gsap) {
      this.#textBlocks.forEach((textBlock, index) => {
        if (gsap) {
          gsap.set(textBlock, { autoAlpha: index === segmentIndex ? 1 : 0, y: 0 });
        } else {
          textBlock.style.opacity = index === segmentIndex ? '1' : '0';
        }
      });
      return;
    }

    const { textCrossfadeDurationSeconds, textCrossfadeEase } = CONTROLLER_CONFIGURATION;
    const incomingTextBlock = this.#textBlocks[segmentIndex];
    const outgoingTextBlock = this.#textBlocks[previousIndex];

    if (outgoingTextBlock && outgoingTextBlock !== incomingTextBlock) {
      gsap.to(outgoingTextBlock, {
        autoAlpha: 0,
        y: -this.#textRevealDistancePx,
        duration: textCrossfadeDurationSeconds,
        ease: textCrossfadeEase,
        overwrite: 'auto',
      });
    }

    gsap.fromTo(
      incomingTextBlock,
      { autoAlpha: 0, y: this.#textRevealDistancePx },
      {
        autoAlpha: 1,
        y: 0,
        duration: textCrossfadeDurationSeconds,
        ease: textCrossfadeEase,
        overwrite: 'auto',
      }
    );
  }

  /* ----- Reduced motion ----- */

  #applyStaticState() {
    this.#textBlocks.forEach((textBlock, index) => {
      textBlock.style.opacity = index === 0 ? '1' : '0';
      textBlock.style.visibility = index === 0 ? 'visible' : 'hidden';
    });
    this.#indicators.forEach((indicator, index) => {
      indicator.classList.toggle('is-active', index === 0);
    });
    this.#frameAnimation?.ready.then(() => this.#frameAnimation?.seek(0));
  }

  /* ----- Settings ----- */

  #readRevealDistance() {
    const cssValue = getComputedStyle(this).getPropertyValue('--kb-reveal-distance');
    const parsed = parseFloat(cssValue);
    return Number.isFinite(parsed) ? parsed : CONTROLLER_CONFIGURATION.fallbackTextRevealDistancePx;
  }

  /** Read the original (desktop) frame count + start index from the engine element. */
  #readFrameNumbering() {
    const dataset = this.#frameAnimation?.dataset;
    const count = Number(dataset?.frameCount);
    const startIndex = Number(dataset?.frameStartIndex);
    this.#frameCount = Number.isFinite(count) && count > 0 ? count : 0;
    this.#frameStartIndex = Number.isFinite(startIndex) ? startIndex : 1;
  }

  /** Convert an authored frame number (original numbering) to 0..1 frame progress. */
  #frameNumberToProgress(frameNumber) {
    if (this.#frameCount < 2) return 0;
    const progress = (frameNumber - this.#frameStartIndex) / (this.#frameCount - 1);
    return Math.min(1, Math.max(0, progress));
  }

  /**
   * Parse data-text-float-points ("frame : percent" per line) into float
   * keyframes in progress space, sorted by progress. Percent is the upward
   * offset as a fraction of the banner height (positive = up). Invalid lines are
   * skipped; an empty/absent attribute yields no float.
   * @returns {{ progress: number, percent: number }[]}
   */
  #parseFloatPoints() {
    const raw = this.dataset.textFloatPoints;
    if (!raw || this.#frameCount < 2) return [];

    const points = [];
    raw.split(/[\n;]+/).forEach((line) => {
      const [framePart, percentPart] = line.split(':');
      const frameNumber = Number(framePart);
      const percent = Number(percentPart);
      if (!Number.isFinite(frameNumber) || !Number.isFinite(percent)) return;
      points.push({ progress: this.#frameNumberToProgress(frameNumber), percent });
    });

    return points.sort((a, b) => a.progress - b.progress);
  }

  /**
   * Build the emphasis warp lookup from data-slow-frame-ranges ("a-b, c-d") and
   * data-emphasis-strength. Frames inside the ranges get a higher scroll weight,
   * so they consume more of the runway and linger. Leaves the warp as the
   * identity (null arrays) when no ranges or strength <= 1.
   */
  #buildEmphasisWarp() {
    this.#emphasisScrollPositions = null;
    this.#emphasisFrameProgress = null;

    const strength = Number(this.dataset.emphasisStrength);
    const frameCount = this.#frameCount;
    if (!Number.isFinite(strength) || strength <= 1 || frameCount < 2) return;

    const ranges = this.#parseFrameRanges(this.dataset.slowFrameRanges);
    if (ranges.length === 0) return;

    const isInRange = (frameIndex) => {
      const frameNumber = frameIndex + this.#frameStartIndex;
      return ranges.some(([low, high]) => frameNumber >= low && frameNumber <= high);
    };

    const weights = new Array(frameCount);
    let totalWeight = 0;
    for (let index = 0; index < frameCount; index += 1) {
      const weight = isInRange(index) ? strength : 1;
      weights[index] = weight;
      totalWeight += weight;
    }

    const scrollPositions = new Array(frameCount);
    const frameProgress = new Array(frameCount);
    let accumulated = 0;
    for (let index = 0; index < frameCount; index += 1) {
      // Place each frame at the centre of its weight band in scroll space, so the
      // mapping is monotonic and the ends sit just inside [0, 1].
      scrollPositions[index] = (accumulated + weights[index] / 2) / totalWeight;
      frameProgress[index] = index / (frameCount - 1);
      accumulated += weights[index];
    }

    this.#emphasisScrollPositions = scrollPositions;
    this.#emphasisFrameProgress = frameProgress;
  }

  /** Parse "a-b, c-d" (or single "a") frame ranges into [[low, high], ...] (frame numbers). */
  #parseFrameRanges(raw) {
    if (!raw) return [];
    const ranges = [];
    raw.split(/[,\n;]+/).forEach((part) => {
      const bounds = part.split('-').map((value) => Number(value.trim()));
      if (bounds.length === 1 && Number.isFinite(bounds[0])) {
        ranges.push([bounds[0], bounds[0]]);
      } else if (bounds.length === 2 && Number.isFinite(bounds[0]) && Number.isFinite(bounds[1])) {
        ranges.push([Math.min(bounds[0], bounds[1]), Math.max(bounds[0], bounds[1])]);
      }
    });
    return ranges;
  }
}

if (!customElements.get('kb-waitlist-storytelling')) {
  customElements.define('kb-waitlist-storytelling', KBWaitlistStorytelling);
}

export { KBWaitlistStorytelling };
